(() => {
  if (window.ProhealthAutomaticGifts) return;

  const ROOT_SELECTOR = '[data-ph-automatic-gifts]';
  const GIFT_PROPERTY = '_free_gift';
  const OFFER_PROPERTY = '_ph_auto_offer';
  const PROMO_PROPERTY = '_promo';
  const COUPON_STORAGE_KEY = 'minimog-discount-code';
  const COUPON_SUPPRESSION_KEY = 'ph-auto-gift-suppressed-by-coupon';

  let running = false;
  let scheduledTimer = null;
  let lastCouponState = false;
  let productCache = Object.create(null);

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const normalise = (value) => String(value || '').toLowerCase().trim();

  const moduleEl = () => document.querySelector(ROOT_SELECTOR);

  const getSettings = () => {
    const module = moduleEl();
    if (!module) return null;
    const configNode = module.querySelector('[data-ph-automatic-gifts-config]');
    let offers = [];
    try {
      offers = JSON.parse(configNode?.textContent || '[]');
    } catch (error) {
      console.error('Automatic gift configuration error:', error);
    }
    return {
      module,
      enabled: module.dataset.enabled !== 'false',
      disableWithCoupon: module.dataset.disableWithCoupon !== 'false',
      removeIfNotFree: module.dataset.removeIfNotFree !== 'false',
      addedMessage: module.dataset.addedMessage || '',
      removedCouponMessage: module.dataset.removedCouponMessage || '',
      priceErrorMessage: module.dataset.priceErrorMessage || '',
      conflictTitle: module.dataset.conflictTitle || 'Choose your offer',
      conflictMessage: module.dataset.conflictMessage || 'This coupon cannot be combined with the promotional free gift currently in your cart.',
      continueCouponLabel: module.dataset.continueCouponLabel || 'Use coupon and remove gift',
      keepGiftLabel: module.dataset.keepGiftLabel || 'Keep my free gift',
      offers: offers.filter((offer) => offer && offer.enabled !== false)
    };
  };

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to retrieve the latest cart.');
    return response.json();
  };

  const getProduct = async (handle) => {
    if (!handle) return null;
    if (productCache[handle]) return productCache[handle];
    const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'force-cache'
    });
    if (!response.ok) throw new Error(`Unable to read product ${handle}.`);
    const product = await response.json();
    productCache[handle] = product;
    return product;
  };

  const isAutomaticGift = (item) =>
    item?.properties?.[GIFT_PROPERTY] === 'true' && Boolean(item?.properties?.[OFFER_PROPERTY]);

  const giftLinesForOffer = (cart, offerId) =>
    (cart?.items || []).filter(
      (item) => isAutomaticGift(item) && String(item.properties[OFFER_PROPERTY]) === String(offerId)
    );

  const applicableCouponCodes = (cart) => {
    const codes = Array.isArray(cart?.discount_codes)
      ? cart.discount_codes
          .filter((discount) => discount && discount.applicable !== false && discount.code)
          .map((discount) => String(discount.code).trim())
          .filter(Boolean)
      : [];

    if (codes.length) return codes;

    const stored = String(localStorage.getItem(COUPON_STORAGE_KEY) || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
    return stored;
  };

  const hasCoupon = (cart, explicitState) => {
    if (typeof explicitState === 'boolean') return explicitState;
    return applicableCouponCodes(cart).length > 0;
  };

  const qualifyingQuantity = async (cart, offer) => {
    const requiredHandles = new Set(
      String(offer.qualifierHandles || '')
        .split('|')
        .map((handle) => normalise(handle))
        .filter(Boolean)
    );
    const requiredTag = normalise(offer.qualifierTag);
    let total = 0;

    for (const item of cart?.items || []) {
      if (isAutomaticGift(item) || item?.properties?._ph_unlock_gift === 'true') continue;

      if (offer.qualifierType === 'product_tag') {
        if (!requiredTag || !item.handle) continue;
        try {
          const product = await getProduct(item.handle);
          const matches = (product?.tags || []).some((tag) => normalise(tag) === requiredTag);
          if (matches) total += Number(item.quantity || 0);
        } catch (error) {
          console.warn('Automatic gift tag lookup failed:', error);
        }
      } else if (requiredHandles.has(normalise(item.handle))) {
        total += Number(item.quantity || 0);
      }
    }

    return total;
  };

  const desiredGiftQuantity = async (cart, offer, couponSuppressed) => {
    const giftVariantId = Number(offer.giftVariantId || 0);
    const requiredQuantity = Math.max(Number(offer.requiredQuantity || 1), 1);
    const giftQuantity = Math.max(Number(offer.giftQuantity || 1), 1);
    if (!giftVariantId || couponSuppressed) return 0;

    const qualifierQty = await qualifyingQuantity(cart, offer);
    if (qualifierQty < requiredQuantity) return 0;

    const qualifications = offer.repeatOffer === false ? 1 : Math.floor(qualifierQty / requiredQuantity);
    let desired = Math.max(qualifications, 0) * giftQuantity;
    const maxGifts = Number(offer.maxGifts || 0);
    if (maxGifts > 0) desired = Math.min(desired, maxGifts);
    return desired;
  };

  const changeLine = async (key, quantity) => {
    const response = await fetch(`${rootUrl()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Unable to update promotional gift.');
    return response.json();
  };

  const addGift = async (offer, quantity) => {
    const response = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        id: Number(offer.giftVariantId),
        quantity,
        properties: {
          [GIFT_PROPERTY]: 'true',
          [OFFER_PROPERTY]: String(offer.id),
          [PROMO_PROPERTY]: String(offer.name || 'Automatic gift')
        }
      })
    });
    if (!response.ok) throw new Error('Unable to add promotional gift.');
    return response.json();
  };

  const setGiftQuantity = async (cart, offer, desiredQty) => {
    let latest = cart;
    let lines = giftLinesForOffer(latest, offer.id);
    const currentQty = lines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);
    if (currentQty === desiredQty && lines.length <= 1) return { changed: false, added: false };

    if (desiredQty <= 0) {
      let removed = false;
      let safety = 0;
      while (lines.length && safety < 20) {
        latest = await changeLine(lines[0].key, 0);
        removed = true;
        safety += 1;
        lines = giftLinesForOffer(latest, offer.id);
      }
      return { changed: removed, added: false };
    }

    if (!lines.length) {
      await addGift(offer, desiredQty);
      return { changed: true, added: true };
    }

    latest = await changeLine(lines[0].key, desiredQty);
    lines = giftLinesForOffer(latest, offer.id);
    let safety = 0;
    while (lines.length > 1 && safety < 20) {
      latest = await changeLine(lines[1].key, 0);
      safety += 1;
      lines = giftLinesForOffer(latest, offer.id);
    }
    return { changed: true, added: desiredQty > currentQty };
  };

  const waitForAutomaticDiscountSettlement = async (cart, attempts = 5, delay = 220) => {
    let latest = cart;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const gifts = promotionalGiftLines(latest);
      if (!gifts.length || gifts.every((item) => Number(item.final_line_price || 0) <= 0)) return latest;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      latest = await getCart();
    }
    return latest;
  };

  const removeChargedGiftLines = async (cart) => {
    let latest = cart;
    let removed = false;
    let safety = 0;
    while (safety < 20) {
      const chargedGift = (latest?.items || []).find(
        (item) => isAutomaticGift(item) && Number(item.final_line_price || 0) > 0
      );
      if (!chargedGift) break;
      latest = await changeLine(chargedGift.key, 0);
      removed = true;
      safety += 1;
    }
    return removed;
  };

  const refreshThemeCart = () => {
    const drawer = document.querySelector('m-cart-drawer');
    const page = document.querySelector('m-cart');
    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate(true);
    if (page && typeof page.onCartUpdate === 'function') page.onCartUpdate(true);
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        source: 'prohealth-automatic-gifts'
      });
    }
  };

  const notify = (settings, message, type = 'warning') => {
    if (!message) return;
    if (window.MinimogTheme?.Notification?.show) {
      window.MinimogTheme.Notification.show({
        target: document.querySelector('.m-cart-drawer--active [data-ph-cart-notice-host]') ||
          document.querySelector('.m-cart__footer--wrapper [data-ph-cart-notice-host]') ||
          settings.module || document.body,
        type,
        message,
        last: 5000,
        sticky: false
      });
    }
  };

  const suppressedCouponCode = () => String(localStorage.getItem(COUPON_SUPPRESSION_KEY) || '').trim();

  const isSuppressedByCoupon = (cart) => {
    const storedCode = normalise(suppressedCouponCode());
    if (!storedCode) return false;
    const currentCodes = applicableCouponCodes(cart).map(normalise);
    const active = currentCodes.includes(storedCode);
    if (!active) localStorage.removeItem(COUPON_SUPPRESSION_KEY);
    return active;
  };

  const promotionalGiftLines = (cart) => (cart?.items || []).filter(isAutomaticGift);

  const removePromotionalGifts = async (cart = null) => {
    if (running) return { cart: cart || null, removed: false };
    running = true;
    try {
      let latest = cart || await getCart();
      let removed = false;
      let safety = 0;

      // Shopify can regenerate line-item keys whenever discounts are recalculated.
      // Always remove one promotional line from the latest cart response, then
      // re-read the remaining gift line from that fresh response before continuing.
      while (safety < 20) {
        const line = promotionalGiftLines(latest)[0];
        if (!line) break;
        latest = await changeLine(line.key, 0);
        removed = true;
        safety += 1;
      }

      return { cart: latest, removed };
    } finally {
      running = false;
    }
  };

  const setCouponSuppression = (code) => {
    const value = String(code || '').trim();
    if (value) localStorage.setItem(COUPON_SUPPRESSION_KEY, value);
    else localStorage.removeItem(COUPON_SUPPRESSION_KEY);
  };

  const clearCouponSuppression = () => localStorage.removeItem(COUPON_SUPPRESSION_KEY);

  const sync = async ({ cart: suppliedCart = null, forceCouponSuppression = false } = {}) => {
    if (running) return suppliedCart;
    const settings = getSettings();
    if (!settings?.enabled) return suppliedCart;

    running = true;
    try {
      let cart = suppliedCart || await getCart();
      const couponSuppressed = settings.disableWithCoupon && (forceCouponSuppression || isSuppressedByCoupon(cart));
      const giftCountBefore = (cart.items || []).filter(isAutomaticGift).length;
      let changed = false;
      let giftAdded = false;

      for (const offer of settings.offers) {
        const desired = await desiredGiftQuantity(cart, offer, couponSuppressed);
        const result = await setGiftQuantity(cart, offer, desired);
        if (result.changed) {
          changed = true;
          giftAdded = giftAdded || result.added;
          cart = await getCart();
        }
      }

      let priceProtectionRemoved = false;
      if (!couponSuppressed && settings.removeIfNotFree) {
        // When several Buy X Get Y gifts are restored together, Shopify can need a
        // short moment to recalculate all native automatic discounts. Wait for the
        // final cart state before treating any restored gift as chargeable.
        cart = await waitForAutomaticDiscountSettlement(cart);
        priceProtectionRemoved = await removeChargedGiftLines(cart);
        if (priceProtectionRemoved) {
          changed = true;
          cart = await getCart();
        }
      }

      if (changed) refreshThemeCart();

      const giftCountAfter = (cart.items || []).filter(isAutomaticGift).length;
      if (couponSuppressed && giftCountBefore > 0 && giftCountAfter === 0 && !lastCouponState) {
        notify(settings, settings.removedCouponMessage, 'warning');
      } else if (priceProtectionRemoved) {
        notify(settings, settings.priceErrorMessage, 'warning');
      } else if (giftAdded && giftCountAfter > giftCountBefore) {
        notify(settings, settings.addedMessage, 'success');
      }

      lastCouponState = couponSuppressed;
      return cart;
    } catch (error) {
      console.error('Automatic gift error:', error);
      return suppliedCart;
    } finally {
      running = false;
    }
  };

  const scheduleSync = () => {
    window.clearTimeout(scheduledTimer);
    scheduledTimer = window.setTimeout(() => sync(), 350);
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (/\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(requestUrl) && !running) scheduleSync();
    } catch (error) {}
    return response;
  };

  document.addEventListener('DOMContentLoaded', scheduleSync, { once: true });
  document.addEventListener('shopify:section:load', scheduleSync);

  window.ProhealthAutomaticGifts = {
    sync,
    scheduleSync,
    getCart,
    getSettings,
    hasPromotionalGifts: (cart) => promotionalGiftLines(cart).length > 0,
    removePromotionalGifts,
    setCouponSuppression,
    clearCouponSuppression,
    afterCartMutation: (cart) => sync({ cart }),
    afterDiscountMutation: (cart, options = {}) => {
      if (options.suppressCode) setCouponSuppression(options.suppressCode);
      if (options.clearSuppression) clearCouponSuppression();
      return sync({ cart, forceCouponSuppression: Boolean(options.suppressCode) });
    }
  };
})();
